import { clamp, isEmpty, matches } from 'lodash';
import type { ApolloCache } from '@apollo/client';
import type { ModifierWithRefs } from '@frameio/graphql-client';
import type {
  Account,
  AccountUser,
  Query,
} from '@frameio/graphql-client/generated';

export const updateCacheAfterUserDeletion = (
  cache: ApolloCache<any>,
  deletedAccountUserId: AccountUser['id'],
  accountId?: Account['id'] | null
) => {
  cache.evict({
    id: cache.identify({
      __typename: 'AccountUser',
      id: deletedAccountUserId,
    }),
  });

  cache.evict({
    id: cache.identify({
      __typename: 'SuggestedAccountUser',
      id: deletedAccountUserId,
    }),
  });

  const decrementAccountUsers = ((value) => {
    const includesUser = value.nodes.find(
      matches({ id: deletedAccountUserId })
    );

    // We want to decrement `accountUsers.totalCount` for all cached instances
    // for which the deleted user is included in the results *or* where `nodes`
    // is empty, indicating that it is most likely being used to provide a
    // canonical, unfiltered `totalCount`.
    if (isEmpty(value.nodes) || includesUser) {
      return {
        ...value,
        totalCount: clamp(value.totalCount - 1, 0, Infinity),
      };
    }

    return value;
  }) satisfies ModifierWithRefs<Query['accountUsers']>;

  cache.modify<Query>({
    id: cache.identify({ __typename: 'Query' }),
    fields: { accountUsers: decrementAccountUsers },
  });

  if (accountId) {
    cache.modify<Account>({
      id: cache.identify({ __typename: 'Account', id: accountId }),
      fields: { accountUsers: decrementAccountUsers },
    });
  }

  cache.gc();
};
